import { useMemo, useCallback } from 'react'
import { AgGridReact } from 'ag-grid-react'
import type { ColDef, GridSizeChangedEvent } from 'ag-grid-community'
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community'
import type { CustomCellRendererProps } from 'ag-grid-react'
import { ArchiveRestore } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { cn } from '@/utils/cn'
import { formatDateShort } from '@/utils/formatters'
import { formatUserDisplayName } from '@/utils/formatters'
import { getStatusBadgeClassName, getStatusLabel } from '@/utils/statusConfig'
import type { ApiPlanSummary, ApiPlanStatus } from '@/types'

ModuleRegistry.registerModules([AllCommunityModule])

function TextCell(props: CustomCellRendererProps<ApiPlanSummary>) {
  return (
    <span className="text-sm text-foreground line-clamp-2">
      {props.value as string}
    </span>
  )
}

function StatusBadgeCell(props: CustomCellRendererProps<ApiPlanSummary>) {
  const status = props.value as ApiPlanStatus
  if (!status) return null

  return (
    <Badge
      variant="outline"
      className={cn(
        'border-transparent font-medium',
        getStatusBadgeClassName(status)
      )}
    >
      {getStatusLabel(status)}
    </Badge>
  )
}

function OwnerCell(props: CustomCellRendererProps<ApiPlanSummary>) {
  const plan = props.data
  if (!plan) return null

  return (
    <span className="text-sm text-foreground line-clamp-2">
      {formatUserDisplayName(plan.owner)}
    </span>
  )
}

function DeletedAtCell(props: CustomCellRendererProps<ApiPlanSummary>) {
  const value = props.value as string | null | undefined
  if (!value) return <span className="text-sm text-muted-foreground">-</span>

  return (
    <span className="text-sm text-foreground">{formatDateShort(value)}</span>
  )
}

function DeletedByCell(props: CustomCellRendererProps<ApiPlanSummary>) {
  const plan = props.data
  if (!plan) return null

  return (
    <span className="text-sm text-foreground line-clamp-2">
      {plan.deleted_by ? formatUserDisplayName(plan.deleted_by) : '-'}
    </span>
  )
}

interface ActionsCellProps extends CustomCellRendererProps<ApiPlanSummary> {
  onRestorePlan: (plan: ApiPlanSummary) => void
}

function ActionsCell(props: ActionsCellProps) {
  const plan = props.data
  if (!plan) return null

  const { onRestorePlan } = props

  return (
    <TooltipProvider delayDuration={300}>
      <div className="flex items-center justify-center gap-1">
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 cursor-pointer"
              onClick={() => onRestorePlan(plan)}
            >
              <ArchiveRestore className="h-4 w-4 text-muted-foreground" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>Restore Plan</TooltipContent>
        </Tooltip>
      </div>
    </TooltipProvider>
  )
}

interface DeletedPlansGridProps {
  plans: ApiPlanSummary[]
  onRestorePlan: (plan: ApiPlanSummary) => void
  loading?: boolean
}

export function DeletedPlansGrid({
  plans,
  onRestorePlan,
  loading,
}: DeletedPlansGridProps) {
  const columnDefs: ColDef<ApiPlanSummary>[] = useMemo(
    () => [
      {
        field: 'name',
        headerName: 'Plan Name',
        flex: 2,
        minWidth: 200,
        cellRenderer: TextCell,
      },
      {
        field: 'owner',
        headerName: 'Owner',
        flex: 1.2,
        minWidth: 140,
        cellRenderer: OwnerCell,
      },
      {
        field: 'status',
        headerName: 'Status',
        flex: 0.8,
        minWidth: 120,
        maxWidth: 150,
        cellRenderer: StatusBadgeCell,
      },
      {
        field: 'deleted_at',
        headerName: 'Deleted On',
        flex: 1,
        minWidth: 130,
        maxWidth: 160,
        cellRenderer: DeletedAtCell,
      },
      {
        field: 'deleted_by',
        headerName: 'Deleted By',
        flex: 1.2,
        minWidth: 140,
        cellRenderer: DeletedByCell,
      },
      {
        headerName: 'Actions',
        flex: 0.8,
        minWidth: 100,
        maxWidth: 120,
        cellClass: 'ag-center-aligned-cell',
        headerClass: 'ag-center-aligned-header',
        cellRenderer: ActionsCell,
        cellRendererParams: {
          onRestorePlan,
        },
      },
    ],
    [onRestorePlan]
  )

  const onGridSizeChanged = useCallback((params: GridSizeChangedEvent) => {
    setTimeout(() => {
      params.api.sizeColumnsToFit()
    }, 10)
  }, [])

  return (
    <div className="w-full">
      <div className="border border-[#e7e7e7] bg-white overflow-hidden">
        <div className="ag-theme-michaels w-full">
          <AgGridReact<ApiPlanSummary>
            rowData={plans}
            columnDefs={columnDefs}
            theme="legacy"
            domLayout="autoHeight"
            rowHeight={68}
            headerHeight={48}
            loading={loading}
            autoSizeStrategy={{ type: 'fitGridWidth' }}
            onGridSizeChanged={onGridSizeChanged}
            overlayLoadingTemplate="<span>Loading...</span>"
            overlayNoRowsTemplate="<span>No deleted plans found</span>"
            suppressCellFocus={true}
            suppressRowHoverHighlight={false}
            suppressColumnVirtualisation={true}
            suppressMovableColumns={true}
          />
        </div>
      </div>
    </div>
  )
}
